import { Component, OnInit, OnDestroy } from '@angular/core';
import { Platform, ToastController } from '@ionic/angular';
import { App } from '@capacitor/app';
import { Router, NavigationEnd } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { PushService } from './core/services/push.services';

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
  standalone: false,
})
export class AppComponent implements OnInit, OnDestroy {
  private lastBackPress = 0;
  private backButtonSubscription?: Subscription;
  private routerSubscription?: Subscription;
  private deferredPrompt: any = null;
  private beforeInstallHandler = (ev: any) => this.onBeforeInstallPrompt(ev);
  private appInstalledHandler = () => this.onAppInstalled();

  constructor(
    private platform: Platform,
    private toastController: ToastController,
    private router: Router,
    private pushService: PushService
  ) {}

  ngOnInit() {

    this.setupBackButtonHandler();
    this.setupNavigationReset();
    this.initializeApp();
    // PWA install prompt handling
    try {
      window.addEventListener('beforeinstallprompt', this.beforeInstallHandler as EventListener);
      window.addEventListener('appinstalled', this.appInstalledHandler as EventListener);
    } catch (e) { /* ignore */ }
  }

  ngOnDestroy() {
    this.backButtonSubscription?.unsubscribe();
    this.routerSubscription?.unsubscribe();
    try {
      window.removeEventListener('beforeinstallprompt', this.beforeInstallHandler as EventListener);
      window.removeEventListener('appinstalled', this.appInstalledHandler as EventListener);
    } catch (e) { /* ignore */ }
  }

  private isAlreadyInstalled(): boolean {
    try {
      const persisted = localStorage.getItem('pwa_installed') === 'true';
      const standalone = (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) || (navigator as any).standalone === true;
      return persisted || !!standalone;
    } catch (e) { return false; }
  }

  private onBeforeInstallPrompt(ev: any) {
    if (this.isAlreadyInstalled()) return;
    ev.preventDefault();
    this.deferredPrompt = ev;
    this.showInstallToast();
  }

  private async showInstallToast() {
    const toast = await this.toastController.create({
      message: 'Install Kurunji LMS App for quick access',
      position: 'bottom',
      buttons: [
        {
          text: 'Install',
          handler: async () => {
            if (!this.deferredPrompt) return;
            try {
              this.deferredPrompt.prompt();
              const choice = await this.deferredPrompt.userChoice;
              if (choice && choice.outcome === 'accepted') {
                localStorage.setItem('pwa_installed', 'true');
              }
            } catch (e) { /* ignore */ }
            this.deferredPrompt = null;
          }
        },
        {
          text: 'Dismiss',
          role: 'cancel'
        }
      ]
    });
    await toast.present();
  }

  private onAppInstalled() {
    try { localStorage.setItem('pwa_installed', 'true'); } catch (e) { /* ignore */ }
  }

  private setupBackButtonHandler() {
    this.backButtonSubscription = this.platform.backButton.subscribeWithPriority(10, async () => {
      const currentTime = Date.now();

      if (currentTime - this.lastBackPress < 2000) {
        // Exit the app
        App.exitApp();
      } else {
        // Show toast and set last back press time
        this.lastBackPress = currentTime;
        const toast = await this.toastController.create({
          message: 'Press back again to exit',
          duration: 2000,
          position: 'bottom'
        });
        await toast.present();
      }
    });
  }

  private setupNavigationReset() {
    this.routerSubscription = this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => {
        // Reset the back press timer on navigation
        this.lastBackPress = 0;
      });
  }

   initializeApp() {
    this.pushService.init();
  }
}
